import dayjs from 'dayjs'
import _ from 'lodash'
import Swal, { type SweetAlertIcon, type SweetAlertOptions } from 'sweetalert2'

// http://colorbrewer2.org/#type=diverging&scheme=RdYlBu&n=7
export const StoolColormap = {
  1: '#4575b4',
  2: '#91bfdb',
  3: '#e0f3f8',
  4: '#efefbf',
  5: '#edd187',
  6: '#fc8d59',
  7: '#d73027'
}

export function isStool({ item }: { item: string }) {
  return item && item.match(/stool/i)
}

export function isStoolBloody({ description }: { description: string }) {
  return description.match(/was bloody/i)
}

export function isStoolOffensive({ description }: { description: string }) {
  return description.match(/offensive smelling/i)
}

export function isStoolMucus({ description }: { description: string }) {
  return description.match(/contained mucus/i)
}

export function getStoolColor({ description }: { description: string }) {
  const p = /stool\swas\s[a-zA-Z]+/i
  const stoolColor = description.match(p)
  return stoolColor ? stoolColor[0].split(' ').slice(-1) : null
}

/**
 * Shows a small toast at the top of the screen. Used throughout the app for
 * confirming user actions, i.e. creating, updating, or deleting items.
 *
 * @param {String} type the type of toast, such as success or warning
 * @param {String} title the message to display
 */
export function showToast({
  type = 'success',
  title
}: {
  title: string
  type: SweetAlertIcon
}) {
  const Toast = Swal.mixin({
    toast: true,
    position: 'top',
    showConfirmButton: false,
    timer: 3000
  })

  Toast.fire({ icon: type, title })
}

export function openDialog({
  type = 'success',
  title,
  text,
  btnLabel = 'OK',
  onConfirm
}: {
  type: SweetAlertIcon
  title: string
  text: string
  btnLabel: string
  onConfirm: any
}) {
  Swal.fire(
    {
      title,
      html: `<div style='font-weight: 400'>${text}</div>`,
      icon: type,
      showConfirmButton: true,
      confirmButtonText: `<div><i style="padding-right: 5px" class="octicon octicon-trashcan"></i> ${btnLabel}</div>`,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      cancelButtonClass: 'order-1 lmc-swal-cancel-button',
      confirmButtonClass: 'order-2 lmc-swal-confirm-button',
      isConfirmed: onConfirm
    } as any as SweetAlertOptions /* TODO: resolve typing issues */
  ).then((result) => {
    if (result.value) onConfirm()
  })
}

export function getObjectDiff(object: any, base: any) {
  function getChanges(object: any, base: any) {
    return _.transform(object, (result: any, value: any, key: any) => {
      if (!_.isEqual(value, base[key])) {
        result[key] =
          _.isObject(value) && _.isObject(base[key]) && !_.isArray(value)
            ? getChanges(value, base[key])
            : value
      }
    })
  }
  return getChanges(object, base)
}

export function sortList(list: any[], filter: (item: any) => boolean) {
  if (!list) return null
  const withFullNames = list.map((row: any) => ({
    ...row,
    name: `${row.name.first} ${row.name.last}`
  }))
  return _.chain(withFullNames).sortBy('name').filter(filter).value()
}

export function formatAddress(address: any) {
  return _.compact([
    address.number,
    address.name,
    address.street1,
    address.street2,
    address.suburb,
    address.state,
    address.postcode,
    address.country
  ]).join(', ')
}

/**
 * Recursively builds up a FormData object from nested form fields. Support
 * Arrays, plain Objects, Files, Strings, Numbers, and null values.
 *
 * @param {Object} fields includes form keys and values
 * @param {FormData} form FormData object that form values are appended to
 * @param {String} parentKey for nested fields, include the parent key to
 * derive the child key.
 */
export function buildEncodedForm(fields: any[], form: any, parentKey: any) {
  const formData = form || new FormData()

  for (const field in fields) {
    const formKey = parentKey ? `${parentKey}[${field}]` : field
    const formValue = fields[field]

    if (formValue instanceof Array) {
      // append recursively for each array element
      if (typeof formValue[0] === 'object' && formValue[0] !== null) {
        formValue.forEach((arrayValue, idx) => {
          buildEncodedForm(arrayValue, formData, `${formKey}[${idx}]`)
        })
      } else if (formValue.length) {
        formValue.forEach((arrayValue, idx) => {
          const appendValue = arrayValue === null ? 'null' : arrayValue
          formData.append(`${formKey}[${idx}]`, appendValue)
        })
      } else {
        formData.append(formKey, formValue)
      }
    } else if (typeof formValue === 'object' && formValue !== null) {
      if (formValue.file instanceof File) {
        formData.append(formKey, formValue.file)
      } else {
        // append recursively for each property
        buildEncodedForm(formValue, formData, formKey)
      }
    } else if (formValue === null) {
      formData.append(formKey, 'null')
    } else {
      formData.append(formKey, formValue)
    }
  }

  return formData
}

export function getRiskLevel(ranges: any[], score: number) {
  for (const range of ranges) {
    if (score >= range.low && score < range.high) {
      return range.risk
    }
  }
  return 'na'
}

/**
 * Used during componentDidUpdate lifecycle method. Checks current vs previous
 * field values for changes in the provided `valueFields`. We only want an
 * aggregate field (i.e. a 'field' containing multiple Field components)
 * to set a new value if all of its child fields have a value, and at least
 * one of those fields has changed.
 *
 * @param {Object} prevValues previous form fields
 * @param {Object} currentValues current form fields
 * @param {Array} valueFields check if changes have occurred in these fields
 */
export function shouldAggregateFieldSetValue(
  prevValues: any,
  currentValues: any,
  valueFields: any[]
) {
  let hasChangedFields = false

  if (!prevValues || !currentValues) {
    return false
  }

  for (const field of valueFields) {
    if (!_.has(currentValues, field)) {
      return false
    }
    if (_.get(prevValues, field) !== _.get(currentValues, field)) {
      hasChangedFields = true
    }
  }

  return hasChangedFields
}

export function calculateBmi(
  weight: string | undefined | null,
  height: string | undefined | null
) {
  if (!weight || !height) return null
  return parseFloat(
    (parseFloat(weight) / (parseFloat(height) / 100) ** 2).toFixed(1)
  )
}

// Used for generating a handover note at the end of an initial assessment
export function getNewResidentHandoverNotes({
  resident,
  overrideDate,
  contactsInformed
}: {
  resident: any
  overrideDate: dayjs.Dayjs | Date | string | undefined | null
  contactsInformed: boolean
}) {
  const { admissionDate, location, name } = resident

  let note = `${name.first} ${name.last} is joining us`

  if (overrideDate || admissionDate) {
    let date
    const chosenDate = overrideDate || admissionDate
    const difference = dayjs(chosenDate)
      .startOf('day')
      .diff(dayjs().startOf('day'), 'days')
    if (difference === 0) {
      note += ' today. '
    } else if (difference === 1) {
      note += ' tomorrow. '
    } else {
      date = dayjs(chosenDate).format('Do MMMM YYYY')
      note += ` on ${date}. `
    }
  } else {
    note += '. '
  }

  const locationItems = ['building', 'floor', 'room', 'bed']
  const numberOfLocationItems = location && Object.keys(location).length
  if (numberOfLocationItems) {
    note += 'They will be in '
    locationItems.forEach((item, i) => {
      const isLastItem =
        item === Object.keys(location)[numberOfLocationItems - 1]
      const hasValue = location[item] || location[item] === 0
      if (hasValue && isLastItem) {
        note += `${item}: ${location[item]}. `
      } else if (hasValue) {
        note += `${item}: ${location[item]}, `
      }
    })
  }

  if (contactsInformed) {
    note += 'Their family / social worker has been informed. '
  }

  note += 'Please read their profile so you know how to best care for them.'

  return note
}

/**
 * Important function that transforms form fields with a `dependsOn` value into
 * 'child' fields, nested inside the field that they depend on. This data
 * structure is important to maintain for the `UserGeneratedFieldLayout`
 * component, where recursive rendering is used to display dependent fields
 * directly below the fields that they are dependent on.
 *
 * @param {Array} fields form fields in a user-generated form specification,
 * created with the FormBuilder library.
 */
export function groupDependentFields(fields: any[]) {
  const withDependsOn = fields.map((field: any) => {
    if (field.dependsOnKey) {
      return {
        ...field,
        dependsOn: {
          key: field.dependsOnKey,
          value: field.dependsOnValue
        },
        dependsOnKey: undefined,
        dependsOnValue: undefined
      }
    }
    return field
  })
  const byDependsOnKey = _.groupBy(withDependsOn, 'dependsOn.key')
  return _(withDependsOn)
    .filter((f) => !f.dependsOn)
    .map((field) => {
      const key = field.key || field.keys
      if (byDependsOnKey[key]) {
        return {
          ...field,
          dependentFields: byDependsOnKey[key]
        }
      }
      return field
    })
    .value()
}

export function downloadPdfData(data: any, fileName: string) {
  const blob = new Blob([data], { type: 'application/pdf' })
  const link = document.createElement('a')
  const url = window.URL.createObjectURL(blob)
  link.href = url
  link.download = `${fileName}.pdf`

  document.body.appendChild(link)
  link.click()

  setTimeout(() => {
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }, 100)
}

/**
 * Used inside Sidebar to navigate to the correct path when clicking on a
 * sidebar item. The correct path is often different and applying a single rule
 * to all scenarios unfortunately isn't possible.
 *
 * @param params react-router params for the current route
 */
export function getListPath(currentPath: string, params: any) {
  const currentScreen = currentPath.split('/').pop()

  if (!params) {
    return currentScreen
  }

  if (params.reporting_id) {
    return `charts/${params.reporting_id}`
  }
  if (params.care_plan_id) {
    return 'care-plans'
  }
  if (params.body_map_report_id) {
    return `charts/body_maps`
  }
  if (/charts\/body_maps/.test(currentPath)) {
    return 'charts/body_maps'
  }
  if (/emar\/stock/.test(currentPath)) {
    return 'emar/stock'
  }
  if (params.medication_id) {
    return 'emar'
  }
  if (params.pre_assessment_id) {
    return 'initial-assessments'
  }
  if (params.risk_assessment_id) {
    return 'risk-assessments'
  }
  if (params.pre_assessment_id) {
    return 'initial-assessments'
  }
  if (/profile\/edit/.test(currentPath)) {
    return 'profile'
  }
  if (/profile\/medications/.test(currentPath)) {
    return 'profile/medications'
  }
  return currentScreen
}

/**
 * Generates search results for async select box
 *
 * @param {String} input user-generated input from typing
 * @param {Array} options all options for the select box
 * @param {Function} callback function to return the results to
 */
export function getSelectSearchResults(
  input: string,
  options: any[],
  callback: (results: any[]) => void
) {
  if (!input) return callback([])

  const inputValue = input.toLowerCase()
  const search = new RegExp(inputValue)

  const matchedResults = options.filter((option) => {
    return search.test(option.searchValue)
  })

  const sortedByMatchingStart = _.sortBy(matchedResults, (option) => {
    return option.searchValue.startsWith(inputValue) ? 1 : 2
  })

  return callback(_.take(sortedByMatchingStart, 200))
}

export function getLogRevisionChangelog(current: any, previous: any) {
  const result: any[] = []

  if (!_.isPlainObject(current) || !_.isPlainObject(previous)) {
    return result
  }
  if (current.title !== previous.title) {
    result.push('Title was changed')
  }
  if (current.description !== previous.description) {
    result.push('Description was changed')
  }

  if (current.timeLogged !== previous.timeLogged) {
    result.push('Time was changed')
  }

  const currentAmountType = current.amountType || ''
  const previousAmountType = previous.amountType || ''
  if (current.amount1 !== previous.amount1) {
    result.push(
      `Measurement was changed. Was ${previous.amount1}${previousAmountType}, now ${current.amount1}${currentAmountType}`
    )
  }
  if (current.amount2 !== previous.amount2) {
    result.push(
      `Measurement was changed. Was ${previous.amount2}${previousAmountType}, now ${current.amount2}${currentAmountType}`
    )
  }

  if (current.isNextHandover && !previous.isNextHandover) {
    result.push('Flagged for handover')
  }
  if (!current.isNextHandover && previous.isNextHandover) {
    result.push('Unflagged for handover')
  }

  const currentPictures = getRevisionPictures(current)
  const previousPictures = getRevisionPictures(previous)
  const currentNumberPictures = currentPictures.length
  const previousNumberPictures = previousPictures.length
  if (previousNumberPictures - currentNumberPictures === 1) {
    result.push('Photo was deleted')
  } else if (previousNumberPictures - currentNumberPictures > 1) {
    result.push('Photos were deleted')
  } else if (currentNumberPictures - previousNumberPictures === 1) {
    result.push('Photo was added')
  } else if (currentNumberPictures - previousNumberPictures > 1) {
    result.push('Photos were added')
  }

  return result
}

const getRevisionPictures = (revision: any) =>
  _.compact([revision.picture1, revision.picture2, revision.picture3])

export function getNominative(nominative: string | undefined, text: string) {
  if (!nominative || !nominative.length) {
    return text || ''
  }
  if (text.includes('s') && text.charAt(text.length - 1) === 's')
    return nominative
  if (nominative.charAt(nominative.length - 1) !== 's') return nominative
  return nominative.slice(0, -1)
}

export function hexToRgbA(hex: string, opacity = 1) {
  let c: string[] | string
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('')
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]]
    }
    c = '0x' + c.join('')
    return `rgba(${[
      ((c as unknown as number) >> 16) & 255,
      ((c as unknown as number) >> 8) & 255,
      (c as unknown as number) & 255
    ].join(',')},${opacity})`
  }
  throw new Error('Bad Hex')
}

/**
    Validate Hex
    ==============================

    @param {String} hex

    1. remove hash if present
    2. convert from 3 to 6 digit color code & ensure valid hex
*/

export function validateHex(color: string) {
  const hex = color.replace('#', '')

  if (hex.length === 3) {
    return hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
  }
  if (hex.length !== 6) {
    throw new Error(`Invalid color value provided: "${color}"`)
  }

  return hex
}

/**
    Shade Color
    ==============================

    Takes a hexidecimal color, converts it to RGB and lightens or darkens

    @param {String} color
    @param {Number} opacity (0-100)

    1. do fancy RGB bitwise operations
    2. combine back into a hex value
*/

export function shade(color: string, percent: number) {
  if (!color) return
  const decimalFraction = percent / 100
  const hex = validateHex(color)

  // 1.
  const f = parseInt(hex, 16)
  const t = decimalFraction < 0 ? 0 : 255
  const p = decimalFraction < 0 ? decimalFraction * -1 : decimalFraction

  const R = f >> 16
  const G = (f >> 8) & 0x00ff
  const B = f & 0x0000ff

  // 2.
  return (
    '#' +
    (
      0x1000000 +
      (Math.round((t - R) * p) + R) * 0x10000 +
      (Math.round((t - G) * p) + G) * 0x100 +
      (Math.round((t - B) * p) + B)
    )
      .toString(16)
      .slice(1)
  )
}

// shade helpers
export const lighten = shade
export function darken(color: string, percent: number) {
  return shade(color, percent * -1)
}
